import { JavelinaBaseClient } from './JavelinaBaseClient'

export class JavelinaProviderClient extends JavelinaBaseClient {
    constructor(
        javelinaProxyApiUrl = process.env.JAVELINA_PROXY_API_URL, 
        javelinaProxySecretName = process.env.JAVELINA_PROXY_SECRET_NAME || '',
        axiosOptions
    ) {
        super(javelinaProxyApiUrl, javelinaProxySecretName, axiosOptions)
    }

    /**
     * @description returns care points by Javelina facility id
     * @param {String} id JV Facility ID 
     */
    async listProviderAffiliationsByFacilityID(id) {
        await this.createSecureAxiosInstance()

        const params = {
            FacilityID: id
        }

        let path = this.queryParamsToPathParams(params, `pureProxy/provider/listprovideraffiliations`);

        const response = await this.secureAxiosInstance.get(path);

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    /**
     * @description returns provider info by NPI
     * @param {String} npi npi of the provider
     */
    async searchPhysiciansByNationalProviderID(npi) {
        await this.createSecureAxiosInstance()

        const params = {
            NationalProviderID: npi
        }

        let path = this.queryParamsToPathParams(params, `pureProxy/provider/searchPhysicians`);

        const response = await this.secureAxiosInstance.get(path);

        if (response.status !== 200) {
            return null;
        }

        let results = {
            ...response.data,
            Response: {
                Physician: await this.loopPaginationResults("Physician", response.data),
            }
        };
        results.MetaData.RecordCount = results.Response.Physician.length
        return results;
    }

    /**
     * @description returns detailed provider info by Javelina Physican Id
     * @param {String} id JV Physican ID 
     */
    async getPhysicianByPhysicanID(id) {
        await this.createSecureAxiosInstance()

        const params = {
            PhysicianID: id
        }

        let path = this.queryParamsToPathParams(params, `pureProxy/provider/physicians`);

        const response = await this.secureAxiosInstance.get(path);

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    /**
     * @description returns care points by Javelina physican id
     * @param {String} id JV Physican ID 
     */
    async listProviderAffiliationsByPhysicianID(id) {
        await this.createSecureAxiosInstance()

        const params = {
            PhysicianID: id
        }

        let path = this.queryParamsToPathParams(params, `pureProxy/provider/listprovideraffiliations`);

        const response = await this.secureAxiosInstance.get(path);

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    /**
     * @description get all facility networks affiliations by FaciliyID
     * @param {String} facilityID 
     * @returns {Promise<*>} response.FacilityNetworkAffl { FacilityID, FacilityName, TaxID, NetworkAffiliations: [] }
     */
    async listFacilityNetworkAffiliations(facilityID) {
        await this.createSecureAxiosInstance()
        const params = {
            FacilityID: facilityID,
            NetworkAfflType: 'F'
        }
        let path = this.queryParamsToPathParams(params, `pureProxy/provider/listFacilityNetworkAffiliations`);
        const response = await this.secureAxiosInstance.get(path);
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @description post new jv physician
     * @param {Object} addPhysicianBody Jv AddPhysician post body
     * @returns PhysicianID PhysicianCertNumber
     */
    async addNewPhysician(addPhysicianBody) {
        await this.createSecureAxiosInstance();
        let reqBody = {
            "CreatePhysicianRqst": {
                "Physician": {
                    ...addPhysicianBody
                }
            }
        }
        const response = await this.secureAxiosInstance.post(`pureProxy/provider/AddPhysician`, reqBody);
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @description update existing jv physician
     * @param {Object} updatePhysicianBody JV UpdatePhysician put body
     * @param {String} physicianID
     * @returns PhysicianID PhysicianCertNumber
     */
    async updatePhysician(updatePhysicianBody, physicianID) {
        await this.createSecureAxiosInstance();
        let reqBody = {
            "UpdatePhysicianRqst": {
                "PhysicianID": physicianID,
                "Physician": {
                    ...updatePhysicianBody
                }
            }
        }
        const response = await this.secureAxiosInstance.put(`pureProxy/provider/UpdatePhysician`, reqBody);
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @description post new jv physician facility affiliation
     * @param {Object} addAffiliationBody Jv AddFacilityPhysicianAffiliation post body
     * @param {String} facilityID of a facility record in jv
     * @param {String} physicianID of a physician record in jv to affiliate the facility with.
     * @returns ConnectorID
     */
    async addFacilityPhysicianAffiliation(addAffiliationBody, facilityID, physicianID) {
        await this.createSecureAxiosInstance();
        let reqBody = {
            "CreateConnectorRecordRqst": {
                "FacilityID": facilityID,
                "PhysicianID": physicianID,
                "FacilityPhysicianAffl": {
                    ...addAffiliationBody
                }
            }
        }
        const response = await this.secureAxiosInstance.post(`pureProxy/provider/AddFacilityPhysicianAffiliation`, reqBody);
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
    * @description update physician facility affiliation
    * @param {Object} updateAffiliationBody Jv UpdateFacilityPhysicianAffiliation post body
    * @param {String} connectorID of an existing facility physician affiliation record
    * @param {String} facilityID of an existing facility record in jv
    * @param {String} physicianID of an existing physician record in jv
    * @returns ConnectorID
    */
    async updateFacilityPhysicianAffiliation(updateAffiliationBody, connectorID, facilityID, physicianID) {
        await this.createSecureAxiosInstance();
        let reqBody = {
            "UpdateConnectorRecordRqst": {
                "ConnectorID": connectorID,
                "FacilityID": facilityID,
                "PhysicianID": physicianID,
                "FacilityPhysicianAffl": {
                    ...updateAffiliationBody
                }
            }
        }
        const response = await this.secureAxiosInstance.put(`pureProxy/provider/UpdateFacilityPhysicianAffiliation`, reqBody);
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @description returns JV Facilities by TaxID
     * @param {String} taxID JV Facility TaxID 
     */    
    async searchFacilityByTaxID(taxID) {
        await this.createSecureAxiosInstance()
        const params = {
            TaxID: taxID
        }
        let path = this.queryParamsToPathParams(params, `pureProxy/provider/searchFacilities`);
        const response = await this.secureAxiosInstance.get(path);
        if (response.status !== 200) {
            return null;
        }
        let results = {
            ...response.data,
            Response: {
                Facility: await this.loopPaginationResults("Facility", response.data),
            }
        };
        results.MetaData.RecordCount = results.Response.Facility.length
        return results;
    }

    /**
     * @description returns JV Facilities by TaxID & Address matching CurrentAddress object
     * @param {String} taxID JV Facility TaxID
     * @param {{ address1, state, city, postalcode }} Object Facility CurrentAddress components
     */    
    async searchFacilityByTaxIDAndAddress(taxID, { address1, state, city, postalcode }) {
        await this.createSecureAxiosInstance()
        const params = {
            TaxID: taxID,
            Address1: address1,
            State: state,
            City: city,
            PostalCode: postalcode
        }
        let path = this.queryParamsToPathParams(params, `pureProxy/provider/searchFacilities`);
        const response = await this.secureAxiosInstance.get(path);
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @description returns JV Facilities by FacilityID
     * @param {String} facilityID JV FacilityID
     */
    async getFacilityByFacilityID(facilityID) {
        await this.createSecureAxiosInstance()
        const params = {
            FacilityID: facilityID
        }
        let path = this.queryParamsToPathParams(params, `pureProxy/provider/facilities`);
        const response = await this.secureAxiosInstance.get(path);
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @description post new jv facility record
     * @param {Object} addFacilityPostBody Jv AddFacility post body
     * @param {String} billingAddrID link up billing address
     * @returns FacilityID FacilityName TaxID FacilityCertNumber
     */
    async addNewFacility(addFacilityPostBody, billingAddrID) {
        await this.createSecureAxiosInstance();
        let reqBody = {
            "CreateFacilityRqst": {
                "Facility": {
                    ...addFacilityPostBody
                }
            }
        }
        if (billingAddrID) {
            reqBody.CreateFacilityRqst["BillingAddrID"] = +billingAddrID
        }
        const response = await this.secureAxiosInstance.post(`pureProxy/provider/AddFacility`, reqBody);
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @description update existing jv facility record
     * @param {Object} updateFacilityPutBody /UpdateFacility put body
     * @param {String} facilityID of an existing jv facility record
     * @param {String} billingAddrID link up billing address
     * @returns FacilityID FacilityName TaxID FacilityCertNumber
     */
    async updateFacility(updateFacilityPutBody, facilityID, billingAddrID) {
        await this.createSecureAxiosInstance();
        let reqBody = {
            "UpdateFacilityRqst": {
                "FacilityID": facilityID,
                "Facility": {
                    ...updateFacilityPutBody
                }
            }
        }
        if (billingAddrID) {
            reqBody.UpdateFacilityRqst["BillingAddrID"] = +billingAddrID
        }
        const response = await this.secureAxiosInstance.put(`pureProxy/provider/UpdateFacility`, reqBody);
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
    * @description post new jv facility network affiliation
    * @param {Object} addFacilityNetwork Jv AddFacilityNetwork post body
    * @param {String} facilityID of an existing jv facility record
    * @returns FacilityID
    */
    async addNewFacilityNetwork(addFacilityNetwork, facilityID) {
        await this.createSecureAxiosInstance();
        let reqBody = {
            "CreateNetworkAffiliationsRqst": {
                "FacilityID": facilityID,
                "NetworkAffiliations": {
                    ...addFacilityNetwork
                }
            }
        }
        const response = await this.secureAxiosInstance.post(`pureProxy/provider/AddFacilityNetwork`, reqBody);
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
    * @description update existing facility network affiliation
    * @param {Object} updateFacilityNetwork /UpdateFacilityNetwork put body
    * @param {String} facilityID of an existing facility record
    * @param {String} networkAfflSeqID of an existing facility network affiliation record
    * @returns FacilityID
    */
    async updateFacilityNetwork(updateFacilityNetwork, facilityID, networkAfflSeqID) {
        await this.createSecureAxiosInstance();
        let reqBody = {
            "UpdateNetworkAffiliationsRqst": {
                "FacilityID": facilityID,
                "NetworkAfflSeqID": networkAfflSeqID,
                "NetworkAffiliations": {
                    ...updateFacilityNetwork
                }
            }
        }
        const response = await this.secureAxiosInstance.put(`pureProxy/provider/UpdateFacilityNetwork`, reqBody);
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /* JV Core Billing Apis: Search/Read/Create/Update */ 

    /**
     * @description Get JV Billing addresses by TaxID
     * @param {String} taxID 
     * @returns List of jv billing addresses
     */
    async searchBillingAddressByTaxID(taxID) {
        await this.createSecureAxiosInstance()
        const params = {
            TaxID: taxID
        }
        let path = this.queryParamsToPathParams(params, 'pureProxy/core/searchbillingaddress')
        const response = await this.secureAxiosInstance.get(path);
        if (response.status !== 200) {
            return null;
        }
        let results = {
            ...response.data,
            Response: {
                Result: await this.loopPaginationResults("Result", response.data),
            }
        };
        results.MetaData.RecordCount = results.Response.Result.length
        return results;
    }

    /**
     * @description Get JV Billing address by BillingAddrID
     * @param {String} billingAddrID 
     * @returns Jv billing address - Response {}
     */
    async getBillingAddressByID(billingAddrID) {
        await this.createSecureAxiosInstance()
        const params = {
            BillingAddrID: billingAddrID
        }
        let path = this.queryParamsToPathParams(params, 'pureProxy/core/readbillingaddress')
        const response = await this.secureAxiosInstance.get(path);
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
    * @description create new jv billing address
    * @param {Object} addressBody
    * @returns {Promise<*>}
    */
    async createBillingAddress(addressBody) {
        await this.createSecureAxiosInstance();
        let reqBody = {
            "CreateBillingAddrInfo": {
                "BillingAddrInfo": {
                    "TaxID": addressBody.taxID,
                    "BillingOfficeName": addressBody.billingOfficeName,
                    "BillingAddress": [
                        addressBody.billingAddress
                    ],
                    ...addressBody.defaultFields
                }
            }
        }
        const response = await this.secureAxiosInstance.post('pureProxy/core/createbillingaddress', reqBody);
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
    * @description update existing jv billing address
    * @param {Object} addressBody
    * @param {String} billingAddrID - convert to integer for put schema
    * @returns {Promise<*>} BillingAddrID
    */
    async updateBillingAddress(addressBody, billingAddrID) {
        await this.createSecureAxiosInstance();
        let reqBody = {
            "WriteBillingAddrInfo": {
                "BillingAddrID": +billingAddrID,
                "BillingAddrInfo": {
                    "TaxID": addressBody.taxID,
                    "BillingOfficeName": addressBody.billingOfficeName,
                    "BillingAddress": [
                        addressBody.billingAddress
                    ],
                    ...addressBody.defaultFields
                }
            }
        }
        const response = await this.secureAxiosInstance.put('pureProxy/core/updatebillingaddress', reqBody);
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
    * @description returns provider network affiliations by ConnectorID 
    * @param {String} id JV ConnectorID (generated when affiliating a physician to a facility)
    */
    async listProviderNetworkAffiliationsByConnectorID(id) {
        await this.createSecureAxiosInstance()
        const params = {
            ConnectorID: id
        }
        let path = this.queryParamsToPathParams(params, `pureProxy/provider/listconnectornetworkaffiliations`);
        const response = await this.secureAxiosInstance.get(path);
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
    * @description add new provider network affiliation record
    * @param {Object} affilBody /AddProviderNetwork post body
    * @param {String} id JV ConnectorID (generated when affiliating a physician to a facility)
    */
    async addNewProviderNetwork(affilBody, id) {
        await this.createSecureAxiosInstance();
        let reqBody = {
            "CreateNetworkAffiliationsRqst": {
                "ConnectorID": id,
                "NetworkAffiliations": {
                    ...affilBody
                }
            }
        }
        const response = await this.secureAxiosInstance.post('pureProxy/provider/AddProviderNetwork', reqBody);
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
    * @description update provider network affiliation record
    * @param {Object} affilBody /UpdateProviderNetwork put body
    * @param {string} id JV ConnectorID (generated when affiliating a physician to a facility)
    * @param {string} networkAfflSeqID of affiliation record
    */
    async updateProviderNetwork(affilBody, id, networkAfflSeqID) {
        await this.createSecureAxiosInstance();
        let reqBody = {
            "UpdateNetworkAffiliationsRqst": {
                "ConnectorID": id,
                "NetworkAffiliations": {
                    ...affilBody
                }
            }
        }
        if (networkAfflSeqID) {
            reqBody.UpdateNetworkAffiliationsRqst['NetworkAfflSeqID'] = networkAfflSeqID
        }
        const response = await this.secureAxiosInstance.put('pureProxy/provider/UpdateProviderNetwork', reqBody);
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @description get physician-facility affiliation details by ConnectorId
     * @param {string} connectorId generated when affiliating a physician to a facility
     */
    async getPhysicianFacilityAffiliationByConnectorId(connectorId) {
        await this.createSecureAxiosInstance()
        const params = {
            providerID: connectorId
        }
        const path = this.queryParamsToPathParams(params, 'pureProxy/provider/providerAffiliations')
        const response = await this.secureAxiosInstance.get(path)
        if (response.status !== 200)
            return null

        return response.data
    }
}
